import { types } from 'mobx-state-tree'
import { formatDate } from 'utils/date'

import { CONFIRMED_ICON, ESTIMATED_ICON, PIN_ICON, TIconType } from '../ActivityFeedIcon'
import { FORTO_AVATAR, TAvatarType } from '../avatars/AvatarType'
import { COMPANY_NAME } from '../constants'
import { COLLAPSE_TABLE, IActivityMessageSection, IActivityTitle, ICON, TEXT } from '../messages/MessageSectionType'
import { Activity } from './Activity'

const formatDelay = (delay: number): string => {
  return delay ? ` (${delay > 0 ? '+' : ''}${delay}d)` : ''
}

const ContainersUpdate = types.model('ContainersUpdate', {
  containerNumber: types.maybe(types.string),
  trackingId: types.maybe(types.string),
  date: types.Date,
  reason: '',
  updatedAt: types.Date,
  delay: types.maybe(types.number),
})

export const TransportPlanUpdatedActivity = types
  .compose(
    'TransportPlanUpdatedActivity',
    Activity,
    types.model({
      milestone: types.string,
      state: types.string,
      containers: types.array(ContainersUpdate),
      transportMode: types.string,
      transportType: types.string,
    })
  )
  .preProcessSnapshot((snapshot) => {
    const { containers } = snapshot

    // convert date from string to Date for each container
    return {
      ...snapshot,
      containers: containers
        .map((container) => ({
          ...container,
          date: new Date(container.date),
          updatedAt: new Date(container.updatedAt),
        }))
        .sort((a, b) => b.updatedAt > a.updatedAt),
    }
  })
  .views((self) => ({
    get milestoneText(): string {
      const milestoneModifier = self.state === 'done' ? `${self.milestone}-completed` : `new-date-${self.milestone}`
      const portModifier = self.milestone.match(/^(departure|arrival)$/) && self.transportMode === 'air' ? '_air' : ''
      return `activity-feed_transport-plan-update_${milestoneModifier}${portModifier}`
    },
    get stateText(): string {
      return self.state === 'done' ? '' : `activity-feed_transport-plan-update_${self.state}`
    },
    get stateIcon(): TIconType {
      if (self.state === 'estimated') {
        return ESTIMATED_ICON
      }
      if (self.state === 'confirmed') {
        return CONFIRMED_ICON
      }
      // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'TIconType'.
      return null
    },
    get lastChangedRows(): number[] {
      const mostRecentUpdated: Date = self.containers[0].updatedAt
      return self.containers
        .filter(({ updatedAt }) => updatedAt.getTime() === mostRecentUpdated.getTime())
        .map((element, index) => index)
    },
    get tableContent(): string[][] {
      const hasContainerNumbers = self.containers.filter(({ containerNumber }) => !!containerNumber).length > 0
      const hasReasons = self.containers.filter(({ reason }) => !!reason).length > 0
      const headers = [
        ...(hasContainerNumbers ? ['activity-feed_transport-plan-update_container-number'] : []),
        'activity-feed_transport-plan-update_date',
        ...(hasReasons ? ['activity-feed_transport-plan-update_reason'] : []),
      ]
      const rows: string[][] = self.containers.map(({ containerNumber, date, reason, delay }) => {
        const containerNumberText = containerNumber || ''
        // @ts-expect-error TS(2345) FIXME: Argument of type 'number | null' is not assignable... Remove this comment to see the full error message
        const delayText = formatDelay(delay)
        const showTime = self.milestone === 'delivery'
        const dateText = formatDate(date, showTime)
        return [
          ...(hasContainerNumbers ? [containerNumberText] : []),
          dateText + delayText,
          ...(hasReasons ? [reason] : []),
        ]
      })
      return [headers, ...rows]
    },
  }))
  .views((self) => ({
    get activityIcon(): string {
      return PIN_ICON
    },
    get avatarType(): TAvatarType {
      return FORTO_AVATAR
    },
    get messageContent(): IActivityMessageSection[] {
      return [
        {
          type: TEXT,
          getStyle: (theme) => ({ display: 'block', margin: theme.spacing(0.5, 0, 1.2) }),
          variant: 'body2-medium',
          content: self.milestoneText,
          translate: true,
        },
        {
          type: ICON,
          content: self.stateIcon,
          getStyle: (theme) => ({
            display: 'inline-flex',
            width: 16,
            height: 16,
            transform: 'translateY(2px)',
            marginRight: theme.spacing(0.2),
          }),
          hide: self.state === 'done',
        },
        {
          type: TEXT,
          content: self.stateText,
          translate: true,
        },
        {
          type: COLLAPSE_TABLE,
          tableContent: self.tableContent,
          lastChangedRows: self.lastChangedRows,
        },
      ]
    },
    get title(): IActivityTitle {
      return { text: COMPANY_NAME }
    },
  }))
